/**
 * Device-passkey service.
 *
 * Thin layer between the WebAuthn client and the store. It runs the enroll,
 * list, and remove flows, drives the busy and error flags, and mirrors the
 * enrolled list into the devices slice so the Security section and the send path
 * read a single source of truth. It holds no key and makes no authorization
 * decision; the authenticator holds the key and the gateway verifies.
 */

import {
  listDevices as clientListDevices,
  registerDevice as clientRegisterDevice,
  removeDevice as clientRemoveDevice,
  assertForApproval,
  encodeAssertionHeader,
  isWebAuthnAvailable,
  DeviceCeremonyError,
} from '@/lib/webauthn';
import { ApiError, type AuthRecoveryMode } from '@/lib/api';
import {
  setDevices,
  setDevicesBusy,
  setDevicesError,
  setDevicesSupported,
  hasEnrolledDevice,
  getSlice,
} from '@/store/store';
import type { DeviceCredentialSummary } from '@/lib/types';
import { translate } from '@/i18n';

/** Options carrying an abort signal, matching the other services. */
interface DeviceServiceOptions {
  signal?: AbortSignal;
  authRecovery?: AuthRecoveryMode;
}

/**
 * Turn a thrown error into a calm, user-facing message. A cancelled ceremony,
 * an offline transport, and a gateway error each read differently so the
 * Security section can guide the user rather than surface a raw string.
 *
 * @param err The thrown error.
 * @returns A message suitable for display.
 */
function messageFor(err: unknown): string {
  if (err instanceof DeviceCeremonyError) return err.message;
  if (err instanceof ApiError) {
    if (err.offline) return 'You appear to be offline. Check your connection and try again.';
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong with the device passkey. Try again.';
}

/**
 * Build the X-Device-Assertion header for approving one approval, or an empty
 * header set when the caller has no enrolled device passkey.
 *
 * When no credential is enrolled the approve proceeds exactly as before with no
 * header, so an un-enrolled user is never blocked. When a credential is enrolled
 * the device assertion runs; a user who cancels the prompt or has no working
 * authenticator raises, which the caller lets propagate so nothing is approved
 * without the stamp rather than silently signing. Shared by the send pipeline and
 * the manual approvals list so both approve paths behave the same.
 *
 * @param approvalId The approval being authorized.
 * @param signal Optional abort signal forwarded to the challenge fetch.
 * @param authRecovery Recovery explicitly permitted before WebAuthn advances a
 *   device sign counter.
 * @returns The header map for the approve call, empty when no device is enrolled.
 */
export async function deviceAssertionHeaderForApproval(
  approvalId: string,
  signal?: AbortSignal,
  authRecovery: AuthRecoveryMode = 'refresh-only',
): Promise<Record<string, string>> {
  // "This account has no enrolled device" and "we have not looked yet" are
  // DIFFERENT answers, and only the first may skip the stamp. hasEnrolledDevice()
  // reads the list length alone, so an unloaded slice used to read as unenrolled
  // and quietly approve without an assertion — which the gateway accepts whenever
  // PLANTME_PASSKEY_SIGN_ENFORCE is off, and off is its default.
  //
  // This is reached in ordinary use, not only after an error: the slice starts
  // unloaded on every page load and is reset again at every session boundary, so
  // anyone who approves before the list lands would fall into that window.
  if (!getSlice('devices').loaded) await loadDevices({ signal, authRecovery });

  if (!getSlice('devices').loaded) {
    // The caller is already cancelling, and the approve call immediately after
    // this one aborts on the same signal before anything reaches the gateway.
    // Returning here lets that abort surface instead of a misleading error.
    if (signal?.aborted) return {};
    // Otherwise fail CLOSED. Approving now would send an unstamped approval for
    // an account that may well have a passkey enrolled.
    //
    // This is not one unlucky request: /v1/device/credentials is a GET, so the api
    // layer already retried it config.maxRetries + 1 = 3 times with exponential
    // backoff and jitter, and only a retryable class of failure gets that far. So
    // reaching here means the state genuinely could not be established, which is
    // exactly when refusing is right — not a blip that a fourth attempt would fix.
    throw new DeviceCeremonyError(translate('passkey.stateUnknown'), false);
  }

  if (!hasEnrolledDevice()) return {};
  const assertion = await assertForApproval(approvalId, signal, authRecovery);
  return { 'X-Device-Assertion': encodeAssertionHeader(assertion) };
}

/**
 * The session generation this module's async work belongs to.
 *
 * A device request started under one account must not commit its result after
 * another has begun. The abort on effect cleanup does not cover it: a response
 * that resolves before cleanup runs still writes, repopulating the previous
 * account's credentials after the session reset cleared them.
 */
function sessionGeneration(): number {
  return getSlice('accounts').current.sessionEpoch;
}

/**
 * Load the caller's enrolled device credentials into the store. A no-op that
 * records support as false when WebAuthn is unavailable, so a boot-time load on
 * an unsupported browser leaves the enroll UI hidden without an error. An abort
 * is swallowed so a view teardown does not surface a spurious failure.
 *
 * @param opts Options; the abort signal cancels the in-flight read.
 * @returns The loaded credentials, or an empty list when unsupported.
 */
export async function loadDevices(opts: DeviceServiceOptions = {}): Promise<DeviceCredentialSummary[]> {
  const supported = isWebAuthnAvailable();
  setDevicesSupported(supported);
  if (!supported) {
    setDevices([]);
    return [];
  }
  const generation = sessionGeneration();
  try {
    const rows = await clientListDevices(opts.signal, opts.authRecovery);
    // Discard a response that belongs to a session that has since been replaced,
    // or it would overwrite the reset with the previous account's credentials.
    if (sessionGeneration() !== generation) return [];
    const items: DeviceCredentialSummary[] = rows.map((r) => ({
      id: r.id,
      label: r.label,
      createdAt: r.createdAt,
      lastUsedAt: r.lastUsedAt,
    }));
    setDevices(items);
    return items;
  } catch (err) {
    if (err instanceof ApiError && err.aborted) return [];
    // Same reason: a failure from the previous session must not paint an error
    // over the new one.
    if (sessionGeneration() !== generation) return [];
    setDevicesError(messageFor(err));
    return [];
  }
}

/**
 * Enroll this device under the name the gateway assigns, then reload the list so it
 * appears. Surfaces a calm error on failure, including a user cancellation, and
 * always clears the busy flag.
 *
 * @returns True when the enrollment succeeded.
 */
export async function enrollDevice(): Promise<boolean> {
  setDevicesBusy(true);
  setDevicesError(null);
  try {
    await clientRegisterDevice();
    // Reload from the gateway so the list reflects the server's record rather
    // than an optimistic local guess, which also refreshes hasEnrolledDevice.
    await loadDevices();
    return true;
  } catch (err) {
    setDevicesError(messageFor(err));
    setDevicesBusy(false);
    return false;
  }
}

/**
 * Remove one enrolled credential by its record id, then reload the list. A calm
 * error is surfaced on failure and the busy flag is always cleared.
 *
 * @param id The credential record id.
 * @returns True when the removal succeeded.
 */
export async function unenrollDevice(id: string): Promise<boolean> {
  setDevicesBusy(true);
  setDevicesError(null);
  try {
    await clientRemoveDevice(id);
    await loadDevices();
    return true;
  } catch (err) {
    setDevicesError(messageFor(err));
    setDevicesBusy(false);
    return false;
  }
}
